import { useEffect, useRef, useState } from "react";

const DESKTOP_MQ = "(min-width: 992px)";
const NAV_OFFSET_PX = 88;

function measureLeftColumn() {
  const intro = document.querySelector(".lp2-hero-intro");
  const extras = document.querySelector(".lp2-hero-extras");
  if (!intro || !extras) return null;

  const left = Math.min(
    intro.getBoundingClientRect().left,
    extras.getBoundingClientRect().left,
  );
  const right = Math.max(
    intro.getBoundingClientRect().right,
    extras.getBoundingClientRect().right,
  );

  let top = NAV_OFFSET_PX;
  const vdetail = document.querySelector(".lp2-vdetail-slot .lp2-vdetail");
  if (vdetail) {
    const bottom = vdetail.getBoundingClientRect().bottom;
    if (bottom > top) {
      top = Math.min(bottom + 12, window.innerHeight - 200);
    }
  }

  return {
    position: "fixed",
    top: `${top}px`,
    left: `${left}px`,
    width: `${Math.max(260, right - left)}px`,
    zIndex: 45,
  };
}

/**
 * When the inline price block scrolls off-screen, show a duplicate:
 *   - Desktop: fixed card in the hero left column
 *   - Mobile: fixed bottom row bar
 *
 * On mobile after the customer accepts, `mobileWatchId` switches the pin
 * trigger to the booking form so the sticky bar tracks that flow instead.
 */
export function usePricePin(sourceRef, enabled, { mobileWatchId = null } = {}) {
  const [pinned, setPinned] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [pinStyle, setPinStyle] = useState(null);
  const pinnedRef = useRef(false);
  const isDesktopRef = useRef(false);
  const mobileWatchIdRef = useRef(mobileWatchId);
  mobileWatchIdRef.current = mobileWatchId;

  useEffect(() => {
    if (!enabled) {
      pinnedRef.current = false;
      setPinned(false);
      setIsDesktop(false);
      setPinStyle(null);
      return undefined;
    }

    if (typeof window === "undefined") return undefined;

    const mq = window.matchMedia(DESKTOP_MQ);
    let observer = null;
    let observedEl = null;

    const resolveMobileEl = () => {
      const id = mobileWatchIdRef.current;
      return id ? document.getElementById(id) : sourceRef.current;
    };

    const applyPin = (shouldPin) => {
      const desktop = mq.matches;
      pinnedRef.current = shouldPin;
      isDesktopRef.current = desktop;
      setPinned(shouldPin);
      setIsDesktop(desktop);
      setPinStyle(shouldPin && desktop ? measureLeftColumn() : null);
    };

    const refreshPosition = () => {
      if (!pinnedRef.current || !isDesktopRef.current) return;
      setPinStyle(measureLeftColumn());
    };

    const attach = () => {
      const desktop = mq.matches;
      const el = desktop ? sourceRef.current : resolveMobileEl();
      if (!el) {
        applyPin(false);
        return;
      }
      if (el === observedEl) return;
      if (observer && observedEl) {
        observer.unobserve(observedEl);
      }
      if (!observer) {
        observer = new IntersectionObserver(
          ([entry]) => {
            applyPin(!entry.isIntersecting);
          },
          { threshold: 0, rootMargin: "-72px 0px 0px 0px" },
        );
      }
      observer.observe(el);
      observedEl = el;
      setIsDesktop(desktop);
    };

    attach();
    const retryId = requestAnimationFrame(attach);

    const onMqChange = () => {
      observedEl = null;
      if (pinnedRef.current) {
        applyPin(true);
      } else {
        setIsDesktop(mq.matches);
        setPinStyle(null);
      }
      attach();
    };

    window.addEventListener("scroll", refreshPosition, { passive: true });
    window.addEventListener("resize", refreshPosition);
    window.addEventListener("scroll", attach, { passive: true });
    if (mq.addEventListener) mq.addEventListener("change", onMqChange);
    else mq.addListener(onMqChange);

    return () => {
      cancelAnimationFrame(retryId);
      if (observer) observer.disconnect();
      window.removeEventListener("scroll", refreshPosition);
      window.removeEventListener("resize", refreshPosition);
      window.removeEventListener("scroll", attach);
      if (mq.removeEventListener) mq.removeEventListener("change", onMqChange);
      else mq.removeListener(onMqChange);
    };
  }, [enabled, sourceRef, mobileWatchId]);

  return { pinned, isDesktop, pinStyle };
}
